// FileAttachment — generic file tile rendered inline in the timeline.
//
// One row per file: a mimetype-family icon, the filename (truncated),
// a readable size, and a trailing download button. Click anywhere on
// the tile to download.
//
// The icon is a family glyph rather than a full mimetype mapping — we
// group into (document, spreadsheet, archive, code, generic). Real
// Matrix clients show more variety but also have a larger visual
// footprint; the Nurion brief pins a single uniform tile height.

import type { CSSProperties } from "react";

import { fileFamilyFor, type FileFamily } from "~/lib/file-kind";
import { formatFileSize } from "~/lib/message-utils";
import { sanitizeBlobDownloadUrl } from "~/lib/safe-url";
import { ICON_STROKE } from "~/lib/icon";

export interface FileAttachmentProps {
  /** Human filename to render + use for the download filename attr. */
  name: string;
  /** Fully-resolved download URL produced by `URL.createObjectURL(...)`. */
  href?: string;
  /** Optional lazy resolver used when downloads must not start until click. */
  onDownload?: () => void;
  busy?: boolean;
  /** Mime type from `m.file`.`info.mimetype`; used to pick the glyph. */
  mimetype?: string;
  /** `m.file`.`info.size` in bytes, when known. */
  size?: number;
}

export function FileAttachment({
  name,
  href = "",
  onDownload,
  busy = false,
  mimetype,
  size,
}: FileAttachmentProps) {
  const safeName = name || "Untitled attachment";
  // `FileAttachment` renders file + video downloads; images go to
  // `MediaPreview`, never here. Collapse the `image` branch defensively
  // (a malformed `m.file` with an `image/*` mimetype still gets a
  // sensible generic glyph rather than a panel-wide image icon).
  const family = collapseImage(fileFamilyFor(safeName, mimetype));
  const sizeLabel = formatFileSize(size);
  // Defence in depth: downloads are generated same-origin blobs; anything
  // else renders as a visible but inert attachment tile.
  const safeHref = sanitizeBlobDownloadUrl(href);
  if (!safeHref && onDownload) {
    return (
      <button
        type="button"
        onClick={onDownload}
        disabled={busy}
        className="chat-file-attachment chat-button"
        data-mimetype={mimetype ?? ""}
        aria-label={busy ? `Preparing ${safeName}` : `Download ${safeName}`}
        style={{ ...tileStyle, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 }}
      >
        <FileFamilyIcon family={family} />
        <span style={labelColStyle}>
          <span style={filenameStyle}>{safeName}</span>
          {sizeLabel ? <span style={sizeStyle}>{sizeLabel}</span> : null}
        </span>
        <span aria-hidden style={downloadButtonStyle}>
          <DownloadGlyph />
        </span>
      </button>
    );
  }
  if (!safeHref) {
    // Render a non-anchor tile so the attachment is still visible but
    // not clickable. Users see the filename + size; the download
    // affordance is greyed out.
    return (
      <span className="chat-file-attachment" aria-label={safeName} style={tileStyle}>
        <FileFamilyIcon family={family} />
        <span style={labelColStyle}>
          <span style={filenameStyle}>{safeName}</span>
          {sizeLabel ? <span style={sizeStyle}>{sizeLabel}</span> : null}
        </span>
      </span>
    );
  }
  return (
    <a
      href={safeHref}
      download={safeName}
      target="_blank"
      rel="noopener noreferrer"
      className="chat-file-attachment chat-button"
      data-mimetype={mimetype ?? ""}
      aria-label={`Download ${safeName}`}
      style={tileStyle}
    >
      <FileFamilyIcon family={family} />
      <span style={labelColStyle}>
        <span style={filenameStyle}>{safeName}</span>
        {sizeLabel ? <span style={sizeStyle}>{sizeLabel}</span> : null}
      </span>
      <span aria-hidden style={downloadButtonStyle}>
        <DownloadGlyph />
      </span>
    </a>
  );
}

const tileStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--chat-space-3)",
  width: "100%",
  maxWidth: "var(--chat-file-attachment-max-width)",
  height: "var(--chat-file-attachment-height)",
  padding: "0 var(--chat-file-attachment-padding)",
  borderRadius: "var(--chat-radius-sm)",
  border: "none",
  background: "var(--chat-surface-panel)",
  color: "var(--chat-fg-primary)",
  textDecoration: "none",
  transition: "background var(--chat-motion-fast) var(--chat-motion-ease)",
};

const labelColStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  flex: 1,
  gap: 2,
};

const filenameStyle: CSSProperties = {
  color: "var(--chat-fg-primary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  fontWeight: 300,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const sizeStyle: CSSProperties = {
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
};

const downloadButtonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "var(--chat-file-attachment-download-size)",
  height: "var(--chat-file-attachment-download-size)",
  color: "var(--chat-fg-secondary)",
  flexShrink: 0,
};

type AttachmentFamily = Exclude<FileFamily, "image">;

function collapseImage(family: FileFamily): AttachmentFamily {
  return family === "image" ? "generic" : family;
}

function FileFamilyIcon({ family }: { family: AttachmentFamily }) {
  const baseProps = {
    width: "var(--chat-file-attachment-icon-size)",
    height: "var(--chat-file-attachment-icon-size)",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "var(--chat-fg-secondary)",
    strokeWidth: ICON_STROKE,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    style: { flexShrink: 0 },
  };
  switch (family) {
    case "document":
      return (
        <svg {...baseProps}>
          <path d="M14 3v4a2 2 0 0 0 2 2h4" />
          <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
          <path d="M9 13h6M9 17h4" />
        </svg>
      );
    case "spreadsheet":
      return (
        <svg {...baseProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
        </svg>
      );
    case "archive":
      return (
        <svg {...baseProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M12 7v2M12 11v2M12 15v2" />
        </svg>
      );
    case "code":
      return (
        <svg {...baseProps}>
          <path d="M14 3v4a2 2 0 0 0 2 2h4" />
          <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
          <path d="m10 13-2 2 2 2M14 13l2 2-2 2" />
        </svg>
      );
    case "video":
      // `FileAttachment` renders video events as download tiles (no
      // playback — out of scope). The glyph is a play-triangle in a
      // frame so users recognise the row as a video file rather than
      // a generic document.
      return (
        <svg {...baseProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <polygon points="11 9 16 12 11 15 11 9" />
        </svg>
      );
    case "generic":
    default:
      return (
        <svg {...baseProps}>
          <path d="M14 3v4a2 2 0 0 0 2 2h4" />
          <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
        </svg>
      );
  }
}

function DownloadGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
